<template>
  <DialogHeader>
    <template #right>
      <div
        v-if="!loading"
        class="font-medium text-green-900 cursor-pointer"
        @click="handleSubmit"
      >
        Upload
      </div>
      <Icon
        v-else
        name="Loading"
        :size="14"
        class="stroke-gray-800 animate-spin"
      />
    </template>
    How does this look?
  </DialogHeader>
  <DialogContent>
    <div
      class="w-full h-[130px] flex justify-center items-center rounded-md border border-gray-800/30"
    >
      <div
        class="w-[114px] h-[114px] rounded-full bg-center bg-cover"
        :style="styles"
      />
    </div>
  </DialogContent>
  <DialogFooter>
    <UiButton
      class="w-1/2 tablet-h:w-auto mr-2.5 tablet-h:mr-2"
      :disabled="loading"
      @click="handleChangePhotoClick"
    >
      Change Photo
    </UiButton>
    <UiButton
      class="w-1/2 tablet-h:w-auto"
      :loading="loading"
      primary
      @click="handleSubmit"
    >
      Upload
    </UiButton>
  </DialogFooter>
</template>

<script setup lang="ts">
import { UiButton, Icon } from '@dacr/ui'
import DialogHeader from '@/shared/dialogs/DialogHeader.vue'
import DialogContent from '@/shared/dialogs/DialogContent.vue'
import DialogFooter from '@/shared/dialogs/DialogFooter.vue'
import { useDialog } from '@/shared/dialogs/useDialog'
import { computed, onBeforeMount, ref } from 'vue'
import {
  useCreateAvatarUploadUrlMutation,
  type User,
} from '@/shared/graphql/legacy/generated'
import axios from 'axios'
import { ToastType, useToastStore } from '@/shared/stores/toast'
import { apolloClient } from '@/shared/graphql/client'
import { useUserStore } from '@/shared/stores/user'
import gql from 'graphql-tag'
import { NativeActionsEnum, useWebviewStore } from '@/shared/stores/webview'

const { close } = useDialog()

const props = defineProps<{
  file: File
}>()

const { mutate: createAvatarUploadUrl } = useCreateAvatarUploadUrlMutation()

const toastStore = useToastStore()
const userStore = useUserStore()
const webviewStore = useWebviewStore()

const imageFile = ref(props.file)
const imageDataUrl = ref('')
const loading = ref(false)

const styles = computed(() => ({
  backgroundImage: `url("${imageDataUrl.value}")`,
}))

const processFile = () => {
  const fileReader = new FileReader()

  fileReader.onload = () => {
    imageDataUrl.value = fileReader.result as string
  }

  fileReader.readAsDataURL(imageFile.value)
}

const handleChangePhotoClick = () => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = 'image/*'

  input.style.position = 'absolute'
  input.style.appearance = 'none'
  input.style.right = '99999px'

  document.body.append(input)

  input.click()

  input.onchange = async (event: Event) => {
    document.body.removeChild(input)

    const file = (event.target as HTMLInputElement).files?.[0]

    if (!file) return

    imageFile.value = file

    processFile()
  }
}

const handleSubmit = async () => {
  loading.value = true

  const response = await createAvatarUploadUrl({
    fileName: imageFile.value.name,
  })

  if (!response) return

  const uploadData = response.data?.createAvatarUploadUrl

  if (!uploadData) return

  const { __typename, ...headers } = uploadData.headers

  const formData = new FormData()

  Object.entries(headers).forEach(([key, value]) => {
    if (!value) return

    formData.append(key.replace(/_/g, '-'), value)
  })

  formData.append('file', imageFile.value)

  await axios.post(uploadData.url, formData)

  if (userStore.user) {
    apolloClient.cache.updateFragment<User>(
      {
        id: apolloClient.cache.identify(userStore.user),
        fragment: gql`
          fragment UpdateProfilePicture on User {
            avatarUrl
          }
        `,
      },
      (response) => {
        if (!response || !userStore.user?.avatarUrl) return

        let avatarUrl = ''

        if (userStore.user.avatarUrl.includes('default')) {
          let avatarUrlParts = userStore.user.avatarUrl.split('/').slice(0, -2)
          avatarUrlParts = avatarUrlParts.concat([
            userStore.user.id,
            imageFile.value.name,
          ])

          avatarUrl = avatarUrlParts.join('/')
        } else {
          const avatarUrlParts = userStore.user.avatarUrl.split('/')
          avatarUrlParts[avatarUrlParts.length - 1] = imageFile.value.name

          avatarUrl = avatarUrlParts.join('/')
        }

        return {
          avatarUrl,
        } as User
      }
    )
  }

  if (webviewStore.isActive) {
    webviewStore.postMessage({
      type: NativeActionsEnum.UserUpdated,
      data: {
        user: userStore.user,
      },
    })
  }

  toastStore.showToast({
    type: ToastType.Success,
    content: 'Profile picture updated',
  })

  close()

  loading.value = false
}

onBeforeMount(() => {
  processFile()
})
</script>
